<template>
  <div v-if="isAfecto" class="flex flex-wrap mt-2 -mx-2">
    <div class="w-full mb-2 px-2 flex items-center justify-between">
      <h2 class="form-subtitle mb-0">Renta 5ta - Ingresos de empleador anterior</h2>
      <wz-button v-if="employeeId" variant="primary" size="sm" @click="openCreate">
        Agregar ingresos previos
      </wz-button>
    </div>

    <div class="w-full px-2">
      <wz-alert v-if="!employeeId" variant="info" class="mb-0">
        Guarda primero al colaborador para registrar los ingresos de un empleador anterior.
      </wz-alert>

      <table v-else class="wz-table w-full">
        <thead>
          <tr>
            <th>Año</th>
            <th class="text-right">Sueldos previos</th>
            <th class="text-right">Gratis previas</th>
            <th class="text-right">Retención previa</th>
            <th>Certificado</th>
            <th class="text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="items.length === 0">
            <td colspan="6" class="text-center text-muted">Sin registros.</td>
          </tr>
          <tr v-for="item in items" :key="item.year">
            <td>{{ item.year }}</td>
            <td class="text-right">{{ money(item.priorGrossIncome) }}</td>
            <td class="text-right">{{ money(item.priorGratifications) }}</td>
            <td class="text-right">{{ money(item.priorRetention) }}</td>
            <td>{{ item.certificateNumber || '—' }}</td>
            <td class="text-right">
              <wz-button variant="outline" size="sm" class="mr-1" @click="openEdit(item)">Editar</wz-button>
              <wz-button variant="outline-danger" size="sm" @click="confirmDelete(item)">Eliminar</wz-button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <wz-modal
      v-model="showModal"
      :title="editing ? 'Editar ingresos previos' : 'Agregar ingresos previos'"
      size="lg"
      :close-on-overlay="false"
    >
      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/2 mb-3 px-2">
          <wz-input
            label="Año"
            type="number"
            :disabled="editing"
            v-model="form.year"
            placeholder="Año tributario"
          />
        </div>
        <div class="w-full md:w-1/2 mb-3 px-2">
          <wz-input label="N° de certificado" v-model="form.certificateNumber" placeholder="Opcional" />
        </div>
        <div class="w-full md:w-1/3 mb-3 px-2">
          <wz-input label="Sueldos previos brutos (S/)" type="number" v-model="form.priorGrossIncome" placeholder="0.00" />
        </div>
        <div class="w-full md:w-1/3 mb-3 px-2">
          <wz-input label="Gratificaciones previas (S/)" type="number" v-model="form.priorGratifications" placeholder="0.00" />
        </div>
        <div class="w-full md:w-1/3 mb-3 px-2">
          <wz-input label="Retención previa IR (S/)" type="number" v-model="form.priorRetention" placeholder="0.00" />
        </div>
        <div class="w-full mb-3 px-2">
          <label class="wz-form-label">Notas</label>
          <textarea v-model="form.notes" class="wz-form-control w-full" rows="2" placeholder="Opcional" />
        </div>
        <div class="w-full mb-1 px-2">
          <label class="wz-form-label">
            Certificado de Rentas y Retenciones <small class="text-muted">(PDF, máx. 10MB, opcional)</small>
          </label>
          <wz-file-input
            :key="fileKey"
            class="w-full mt-1"
            id-file="prior-income-cert"
            :accept-file="['.pdf']"
            @onChange="onFileChange"
          />
        </div>
      </div>

      <template #footer>
        <wz-button variant="outline-secondary" @click="showModal = false">Cancelar</wz-button>
        <wz-button variant="primary" :disabled="!canSave || submitting" @click="save">
          {{ submitting ? 'Guardando...' : 'Guardar' }}
        </wz-button>
      </template>
    </wz-modal>
  </div>
</template>

<script>
import { Alert } from '@wizepe/wizeui'
import EmployeePriorIncomeService from '@/core/services/EmployeePriorIncomeService'

export default {
  name: 'PriorIncomeSection',
  props: {
    employeeId: { type: [Number, String], default: null },
    fifthCategoryStatus: { type: String, default: null },
  },
  data() {
    return {
      items: [],
      showModal: false,
      editing: false,
      submitting: false,
      file: null,
      fileKey: 0,
      form: this.emptyForm(),
    }
  },
  computed: {
    isAfecto() {
      return this.fifthCategoryStatus === 'AFECTO'
    },
    canSave() {
      const gross = Number(this.form.priorGrossIncome) || 0
      const grat = Number(this.form.priorGratifications) || 0
      return !!this.form.year && (gross > 0 || grat > 0)
    },
  },
  watch: {
    employeeId: 'load',
    isAfecto(val) {
      if (val) this.load()
    },
  },
  created() {
    if (this.isAfecto && this.employeeId) this.load()
  },
  methods: {
    emptyForm() {
      return {
        year: new Date().getFullYear(),
        priorGrossIncome: null,
        priorGratifications: null,
        priorRetention: null,
        certificateNumber: null,
        notes: null,
      }
    },
    money(v) {
      return `S/ ${Number(v || 0).toFixed(2)}`
    },
    async load() {
      if (!this.employeeId || !this.isAfecto) return
      try {
        const resp = await EmployeePriorIncomeService.list(this.employeeId)
        this.items = resp.payload || []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar los ingresos previos')
      }
    },
    openCreate() {
      this.editing = false
      this.form = this.emptyForm()
      this.file = null
      this.fileKey++
      this.showModal = true
    },
    openEdit(item) {
      this.editing = true
      this.form = {
        year: item.year,
        priorGrossIncome: item.priorGrossIncome,
        priorGratifications: item.priorGratifications,
        priorRetention: item.priorRetention,
        certificateNumber: item.certificateNumber,
        notes: item.notes,
      }
      this.file = null
      this.fileKey++
      this.showModal = true
    },
    onFileChange(files) {
      this.file = files && files.length > 0 ? files[0] : null
    },
    buildFormData() {
      const fd = new FormData()
      fd.append('year', this.form.year)
      fd.append('priorGrossIncome', Number(this.form.priorGrossIncome) || 0)
      fd.append('priorGratifications', Number(this.form.priorGratifications) || 0)
      fd.append('priorRetention', Number(this.form.priorRetention) || 0)
      if (this.form.certificateNumber) fd.append('certificateNumber', this.form.certificateNumber)
      if (this.form.notes) fd.append('notes', this.form.notes)
      if (this.file) fd.append('file', this.file)
      return fd
    },
    async save() {
      try {
        this.submitting = true
        const fd = this.buildFormData()
        const resp = this.editing
          ? await EmployeePriorIncomeService.update(this.employeeId, this.form.year, fd)
          : await EmployeePriorIncomeService.create(this.employeeId, fd)
        Alert.success(this.editing ? 'Ingresos previos actualizados' : 'Ingresos previos registrados')
        if (resp && resp.payload && resp.payload.warning) Alert.alert('Advertencia', resp.payload.warning)
        this.showModal = false
        this.load()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al guardar los ingresos previos')
      } finally {
        this.submitting = false
      }
    },
    confirmDelete(item) {
      Alert.dialog('Eliminar', `¿Eliminar los ingresos previos del año ${item.year}?`, async () => {
        try {
          await EmployeePriorIncomeService.remove(this.employeeId, item.year)
          Alert.success('Registro eliminado')
          this.load()
        } catch (e) {
          Alert.error(e.errors ? e.errors.message : 'Error al eliminar')
        }
      })
    },
  },
}
</script>

<style lang="stylus" scoped></style>
